import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

const initials = (employee) => {
  const name = employee.display_name || employee.username || '';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDueDate = (isoDate) => {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const BASE_COLUMNS = [
  { key: 'key', label: 'Key' },
  { key: 'title', label: 'Title' },
  { key: 'assignees', label: 'Assignee' },
  { key: 'reporter', label: 'Reporter' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'created_at', label: 'Created' },
  { key: 'updated_at', label: 'Updated' },
  { key: 'due_date', label: 'Due date' },
];

const PRIORITY_ORDER = { highest: 0, high: 1, medium: 2, low: 3, lowest: 4 };
const PRIORITY_OPTIONS = [
  { value: 'highest', label: '🔥 Highest' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'lowest', label: 'Lowest' },
];

/**
 * Jira-style flat table of tickets -- an alternative to the Kanban
 * columns for scanning/sorting a lot of tickets at once. Two scopes:
 *
 *  - "This board" (default): reuses the same `tickets` array the Board/
 *    Calendar/Summary tabs already loaded for the currently-open board --
 *    no extra API call, exactly the old behaviour.
 *  - "All sections": fetches tickets across every board (Order
 *    Fulfilment, Other Tasks, Service Desk, ...) in one flat list, with
 *    an extra "Section" column (between Status and Created) showing
 *    which board each row belongs to, plus a dropdown to narrow back
 *    down to just one section without leaving this combined view.
 *
 * Clicking a row opens the same ticket detail modal either way. Sorting
 * here is local to this table (click a header to sort by it) --
 * independent of the board's own "Sort" dropdown, which only affects
 * card order within each Kanban column.
 *
 * Priority and Status are directly editable in place -- each renders as
 * a small dropdown instead of plain text, so a quick reprioritise or
 * status change doesn't require opening the full ticket detail modal
 * (same api.updateTicket / api.moveTicket calls the detail modal itself
 * uses). `onTicketChanged` bubbles the result up to BoardPage so its own
 * Kanban state and tab counts stay in sync even if you made the change
 * from a ticket that isn't on the currently-open board (possible in
 * "All sections" scope).
 */
const ListView = ({ tickets, columns, onOpen, onTicketChanged, boards = [], currentBoardKey, relation, showCompleted, search }) => {
  const [sortKey, setSortKey] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [scope, setScope] = useState('board'); // 'board' | 'all'
  const [sectionFilter, setSectionFilter] = useState('all'); // 'all' | a board key
  const [allTickets, setAllTickets] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [savingIds, setSavingIds] = useState(() => new Set()); // ticket ids currently mid-save, to disable their dropdowns

  // Column -> { name, boardKey, boardName } across every board, so a
  // combined list can show/sort by "Section" and look up each row's
  // status name correctly even though different boards have entirely
  // different column sets. Falls back to just the current board's own
  // columns if the full board list isn't available for some reason.
  const columnMeta = useMemo(() => {
    const map = {};
    if (boards.length > 0) {
      boards.forEach((b) => {
        (b.columns || []).forEach((c) => {
          map[c.id] = { name: c.name, boardKey: b.key, boardName: b.name };
        });
      });
    } else {
      (columns || []).forEach((c) => {
        map[c.id] = { name: c.name, boardKey: currentBoardKey, boardName: null };
      });
    }
    return map;
  }, [boards, columns, currentBoardKey]);

  const columnName = (columnId) => columnMeta[columnId]?.name || '—';
  const sectionName = (columnId) => columnMeta[columnId]?.boardName || '—';

  // Status-dropdown options for a given ticket -- its own board's column
  // set, which differs board to board (e.g. Order Fulfilment's "Shipped"
  // doesn't exist on Service Desk). In "This board" scope every ticket
  // shares the same `columns` prop, so that's the simple/common case.
  const columnsByBoardKey = useMemo(() => {
    const map = {};
    boards.forEach((b) => { map[b.key] = b.columns || []; });
    return map;
  }, [boards]);

  const statusOptionsFor = (ticket) => {
    if (scope !== 'all') return columns || [];
    const boardKey = columnMeta[ticket.column]?.boardKey;
    return columnsByBoardKey[boardKey] || columns || [];
  };

  // Fetches the combined list whenever "All sections" is active and any
  // of the board's own filters change -- mirrors exactly what BoardPage
  // itself passes to api.getTickets for the single-board case.
  useEffect(() => {
    if (scope !== 'all') return undefined;
    let cancelled = false;
    setLoadingAll(true);
    const boardParam = sectionFilter === 'all' ? undefined : sectionFilter;
    api.getTickets(boardParam, search, { relation, showCompleted })
      .then((data) => { if (!cancelled) setAllTickets(data); })
      .catch(() => { if (!cancelled) setAllTickets([]); })
      .finally(() => { if (!cancelled) setLoadingAll(false); });
    return () => { cancelled = true; };
  }, [scope, sectionFilter, relation, showCompleted, search]);

  const sourceTickets = scope === 'all' ? allTickets : tickets;

  const displayColumns = useMemo(() => {
    if (scope !== 'all') return BASE_COLUMNS;
    const withSection = [...BASE_COLUMNS];
    const idx = withSection.findIndex((c) => c.key === 'created_at');
    withSection.splice(idx, 0, { key: 'section', label: 'Section' });
    return withSection;
  }, [scope]);

  const sorted = useMemo(() => {
    const list = [...sourceTickets];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case 'key':
          return dir * a.key.localeCompare(b.key);
        case 'title':
          return dir * a.title.localeCompare(b.title);
        case 'assignees': {
          const an = a.assignees?.[0]?.display_name || '';
          const bn = b.assignees?.[0]?.display_name || '';
          return dir * an.localeCompare(bn);
        }
        case 'reporter': {
          const an = a.reporter?.display_name || '';
          const bn = b.reporter?.display_name || '';
          return dir * an.localeCompare(bn);
        }
        case 'priority':
          return dir * ((PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99));
        case 'status':
          return dir * columnName(a.column).localeCompare(columnName(b.column));
        case 'section':
          return dir * sectionName(a.column).localeCompare(sectionName(b.column));
        case 'due_date': {
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return dir * a.due_date.localeCompare(b.due_date);
        }
        case 'created_at':
          return dir * (new Date(a.created_at) - new Date(b.created_at));
        case 'updated_at':
        default:
          return dir * (new Date(a.updated_at) - new Date(b.updated_at));
      }
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceTickets, sortKey, sortDir, columnMeta]);

  const handleHeaderClick = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const applyUpdatedTicket = (updated) => {
    // Keeps "All sections" mode's own local list current immediately;
    // "This board" mode has no local copy of `tickets` (it's owned by
    // BoardPage), so it relies entirely on onTicketChanged flowing the
    // update back down through the `tickets` prop.
    setAllTickets((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
    onTicketChanged?.(updated);
  };

  const withSaving = async (ticketId, action) => {
    setSavingIds((prev) => new Set(prev).add(ticketId));
    try {
      const updated = await action();
      applyUpdatedTicket(updated);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not save that change.');
    } finally {
      setSavingIds((prev) => { const next = new Set(prev); next.delete(ticketId); return next; });
    }
  };

  const handlePriorityChange = (ticket, priority) => {
    if (priority === ticket.priority) return;
    withSaving(ticket.id, () => api.updateTicket(ticket.id, { priority }));
  };

  const handleStatusChange = (ticket, columnId) => {
    if (String(columnId) === String(ticket.column)) return;
    withSaving(ticket.id, () => api.moveTicket(ticket.id, columnId));
  };

  const renderCell = (ticket, key) => {
    const activeIds = new Set((ticket.active_workers || []).map((e) => e.mavis_user_id));
    switch (key) {
      case 'key':
        return <td key={key}><span className="ticket-key">{ticket.key}</span></td>;
      case 'title':
        return <td key={key}>{ticket.title}</td>;
      case 'assignees':
        return (
          <td key={key}>
            {ticket.assignees?.length ? (
              <span style={{ display: 'flex', gap: '0.2rem' }}>
                {ticket.assignees.map((emp) => (
                  <span
                    key={emp.mavis_user_id} className="avatar"
                    style={activeIds.has(emp.mavis_user_id) ? { boxShadow: '0 0 0 2px var(--danger-soft-border)' } : undefined}
                    title={activeIds.has(emp.mavis_user_id) ? `${emp.display_name} -- working on it now` : emp.display_name}
                  >
                    {initials(emp)}
                  </span>
                ))}
              </span>
            ) : <span style={{ color: '#5b6577' }}>Unassigned</span>}
          </td>
        );
      case 'reporter':
        return <td key={key}>{ticket.reporter?.display_name || '—'}</td>;
      case 'priority':
        return (
          <td key={key} onClick={(e) => e.stopPropagation()}>
            <select
              className={`priority-badge priority-${ticket.priority}`}
              value={ticket.priority}
              disabled={savingIds.has(ticket.id)}
              onChange={(e) => handlePriorityChange(ticket, e.target.value)}
              aria-label={`Priority for ${ticket.key}`}
            >
              {PRIORITY_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </td>
        );
      case 'status':
        return (
          <td key={key} onClick={(e) => e.stopPropagation()}>
            <select
              className="list-inline-select"
              value={ticket.column}
              disabled={savingIds.has(ticket.id)}
              onChange={(e) => handleStatusChange(ticket, Number(e.target.value))}
              aria-label={`Status for ${ticket.key}`}
            >
              {statusOptionsFor(ticket).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </td>
        );
      case 'section':
        return <td key={key} style={{ fontSize: '0.8rem', color: '#aab7c4' }}>{sectionName(ticket.column)}</td>;
      case 'created_at':
        return <td key={key} style={{ fontSize: '0.8rem', color: '#aab7c4' }}>{formatDate(ticket.created_at)}</td>;
      case 'updated_at':
        return <td key={key} style={{ fontSize: '0.8rem', color: '#aab7c4' }}>{formatDate(ticket.updated_at)}</td>;
      case 'due_date':
        return <td key={key} style={{ fontSize: '0.8rem' }}>{formatDueDate(ticket.due_date) || '—'}</td>;
      default:
        return <td key={key} />;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        <div className="segmented-control" style={{ display: 'inline-flex' }}>
          <button type="button" className={scope === 'board' ? 'active' : ''} onClick={() => setScope('board')}>This board</button>
          <button type="button" className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>All sections</button>
        </div>
        {scope === 'all' && boards.length > 0 && (
          <select
            className="form-input" style={{ width: 200 }} value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            aria-label="Filter by section"
          >
            <option value="all">All sections</option>
            {boards.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
          </select>
        )}
        {scope === 'all' && loadingAll && <span style={{ fontSize: '0.8rem', color: '#aab7c4' }}>Loading...</span>}
      </div>

      {sorted.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: '#5b6577', textAlign: 'center', padding: '2rem 0' }}>No tickets.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="list-view-table">
            <thead>
              <tr>
                {displayColumns.map((col) => (
                  <th key={col.key} onClick={() => handleHeaderClick(col.key)} title="Click to sort">
                    {col.label}{sortKey === col.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((ticket) => (
                <tr key={ticket.id} onClick={() => onOpen(ticket.id)} className="list-view-row">
                  {displayColumns.map((col) => renderCell(ticket, col.key))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListView;
